/**
 * Incident Editor Dialog Header Component (PROJ-007)
 * Displays title and description based on create/edit/view mode
 * DRAGGABLE: This header acts as the drag handle for the dialog
 * PROJ-052: Added merge button for view mode
 */

import { DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/draggable-dialog';
import { Button } from '@/components/ui/button';
import { Edit3, GripHorizontal, GitMerge } from 'lucide-react';
import { CanAccess } from '@/components/auth';
import type { IncidentEditorMode } from './types';

interface HeaderProps {
  mode: IncidentEditorMode;
  onSwitchToEdit?: () => void;
  onMerge?: () => void;
  canMerge?: boolean;
}

export function Header({ mode, onSwitchToEdit, onMerge, canMerge }: HeaderProps) {
  const getTitle = () => {
    switch (mode) {
      case 'view':
        return 'Ver Incidente';
      case 'edit':
        return 'Editar Incidente';
      default:
        return 'Nuevo Incidente';
    }
  };

  const getDescription = () => {
    switch (mode) {
      case 'view':
        return 'Información detallada del incidente';
      case 'edit':
        return 'Actualiza la información del incidente';
      default:
        return 'Crea un nuevo incidente en el sistema';
    }
  };

  return (
    <div
      className="drag-handle px-6 py-4 border-b bg-background cursor-move select-none"
      data-dialog-drag-handle="true"
    >
      {/* Visual drag indicator */}
      <div className="flex justify-center mb-2">
        <GripHorizontal className="h-4 w-6 text-muted-foreground/50" />
      </div>
      <DialogHeader className="text-left">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <DialogTitle>{getTitle()}</DialogTitle>
            <DialogDescription>{getDescription()}</DialogDescription>
          </div>
          {mode === 'view' && (
            <div className="flex items-center gap-2 shrink-0">
              {/* PROJ-052: Merge button */}
              {onMerge && canMerge && (
                <CanAccess entity="incidents" privilege="MERGE">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onMerge}
                    className="no-drag text-purple-600 border-purple-200 hover:bg-purple-50 hover:border-purple-300"
                  >
                    <GitMerge className="h-4 w-4 mr-2" />
                    Fusionar
                  </Button>
                </CanAccess>
              )}
              {/* Edit button */}
              {onSwitchToEdit && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onSwitchToEdit}
                  className="no-drag"
                >
                  <Edit3 className="h-4 w-4 mr-2" />
                  Editar
                </Button>
              )}
            </div>
          )}
        </div>
      </DialogHeader>
    </div>
  );
}
